import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { HR_DOCUMENT_CONFIDENTIALITY_LEVELS, HrDocumentType } from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-document-type-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    ToggleSwitchModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent,
    PageDataPanelComponent,
    HrActionDialogComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-document-type-settings.component.html',
  styleUrls: ['./hr-document-type-settings.component.scss']
})
export class HrDocumentTypeSettingsComponent implements OnInit {
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  documentTypes: HrDocumentType[] = [];
  form: HrDocumentType = this.emptyForm();
  formDialogVisible = false;

  workerCategoryOptions: { label: string; value: string }[] = [];
  readonly confidentialityOptions = HR_DOCUMENT_CONFIDENTIALITY_LEVELS.map(value => ({ label: this.format(value), value }));
  readonly canManageDocumentTypes = this.permissionService.hasPermission(app_permissions.hr.documentTypes.manage);

  ngOnInit(): void {
    this.loadWorkerCategories();
    this.load();
  }

  private loadWorkerCategories(): void {
    this.hrService.workerCategories().subscribe(response => {
      this.workerCategoryOptions = (response.data ?? []).map(item => ({
        label: item.categoryName || this.format(item.categoryCode), value: item.categoryCode || ''
      }));
    });
  }

  load(): void {
    this.loading = true;
    this.hrService.documentTypes().subscribe({
      next: response => this.documentTypes = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Documents', detail: 'Unable to load document types.' }),
      complete: () => this.loading = false
    });
  }

  save(): void {
    if (!this.canManageDocumentTypes) return;
    this.hrService.saveDocumentType(this.form).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Documents', detail: 'Document type saved.' });
        this.resetForm();
        this.formDialogVisible = false;
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Documents', detail: 'Unable to save document type.' })
    });
  }

  edit(type: HrDocumentType): void {
    if (!this.canManageDocumentTypes) return;
    this.form = { ...type };
    this.formDialogVisible = true;
  }

  resetForm(): void {
    this.form = this.emptyForm();
  }

  emptyForm(): HrDocumentType {
    return {
      requiredDocument: true,
      requiresNumber: false,
      requiresIssueDate: false,
      requiresExpiryDate: false,
      requiresFile: false,
      requiresVerification: true,
      blocksDispatch: false,
      blocksMobilization: false,
      blocksPayroll: false,
      blocksOnboardingCompletion: false,
      expiryWarningDays: 30,
      confidentialityLevel: 'INTERNAL',
      active: true
    };
  }

  format(value?: string): string {
    return (value || 'All').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }
}
